import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function RolesExplained() {
  const { user } = useAuth();

  const roles = [
    {
      id: 'buyer',
      icon: '🛒',
      title: 'Buyer',
      description: 'Shop for fresh produce from local farms and hubs. Browse products, add to cart, and checkout.',
      bestFor: 'Individuals who want to buy local food',
      course: 'buyer-module-1-welcome',
      courseLabel: 'Buyer Course',
      features: [
        'Browse products from local farms and hubs',
        'Earn credits by completing education modules',
        'Join a Kin to save even more'
      ]
    },
    {
      id: 'kin_coordinator',
      icon: '🌟',
      title: 'Kin Coordinator',
      description: 'Start and lead a buying group (Kin). Invite members, consolidate orders, and coordinate delivery.',
      bestFor: 'Community leaders who want to organize group buying',
      course: 'kin-module-1-what-is-a-kin',
      courseLabel: 'Kin Coordinator Course',
      features: [
        'Create and lead a buying group',
        'Coordinate group orders and deliveries',
        'Build community around local food'
      ]
    },
    {
      id: 'kin_member',
      icon: '👥',
      title: 'Kin Member',
      description: 'Join an existing buying group. Submit your orders and benefit from group discounts.',
      bestFor: 'People who want to join an existing group',
      course: 'kin-module-1-what-is-a-kin',
      courseLabel: 'Kin Member Course',
      features: [
        'Join a group of people buying together',
        'Save 30-40% on food costs',
        'Build community connections'
      ]
    },
    {
      id: 'producer',
      icon: '🚜',
      title: 'Producer',
      description: 'Sell your farm products directly to buyers and Kins. List your products and manage orders.',
      bestFor: 'Farmers and food producers',
      course: 'producer-module-1-welcome',
      courseLabel: 'Producer Course',
      features: [
        'List and manage your products',
        'Connect directly with customers',
        'Build your brand and tell your story'
      ]
    },
    {
      id: 'hub',
      icon: '🏪',
      title: 'Hub',
      description: 'Aggregate products from multiple producers and sell to buyers. Connect local farms with customers.',
      bestFor: 'Businesses that aggregate local produce',
      course: 'hub-module-1-welcome',
      courseLabel: 'Hub Course',
      features: [
        'Partner with multiple producers',
        'Manage products and commissions',
        'Build a local food brand'
      ]
    },
    {
      id: 'pickup',
      icon: '📦',
      title: 'Pickup',
      description: 'Deliver orders from producers and hubs to buyers. Earn credits or cash for each delivery.',
      bestFor: 'Independent delivery drivers',
      course: 'pickup-module-1-welcome',
      courseLabel: 'Pickup Course',
      features: [
        'Flexible delivery schedule',
        'Choose credits or cash payments',
        'Support local food system'
      ]
    },
    {
      id: 'educator',
      icon: '🎓',
      title: 'Educator',
      description: 'Create educational content about food, farming, and nutrition. Build an audience and share your expertise.',
      bestFor: 'Teachers, nutritionists, food writers, and content creators',
      course: 'educator-module-1-welcome',
      courseLabel: 'Educator Course',
      features: [
        'Create educational modules and courses',
        'Build your audience and brand',
        'Partner with producers and hubs'
      ]
    }
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Understanding Our Pickup Roles</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Choose the role that fits you best. You can start with one role and add others later as your involvement grows.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {roles.map((role) => (
          <div key={role.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition border border-gray-100">
            <div className="flex items-start gap-4">
              <div className="text-4xl">{role.icon}</div>
              <div className="flex-1">
                <h2 className="text-xl font-bold mb-1">{role.title}</h2>
                <p className="text-gray-600 text-sm">{role.description}</p>
                <p className="text-xs text-gray-400 mt-1">
                  <strong>Best for:</strong> {role.bestFor}
                </p>

                {/* Features */}
                <ul className="mt-3 space-y-1">
                  {role.features.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-green-500">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Course Link */}
                {role.course && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        📚 <strong>Required:</strong> Complete the {role.courseLabel}
                      </span>
                      <Link
                        to={`/learn/${role.course}`}
                        className="text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1 rounded transition"
                      >
                        Start Learning →
                      </Link>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      💡 Complete this course before registering for this role
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 text-center bg-green-50 border border-green-200 rounded-lg p-6">
        <h2 className="text-xl font-bold text-green-800 mb-2">Ready to get started?</h2>
        <p className="text-green-700 mb-4">
          Choose your role and complete the required course before registering.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link
            to="/register"
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition font-medium"
          >
            Create Your Account →
          </Link>
          <Link
            to="/learn"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            Browse All Courses
          </Link>
        </div>
      </div>
    </div>
  );
}
